import { z } from 'zod'
import { tenantMemberRoleSchema } from './tenant-auth.js'

/**
 * `LGH-040`. Wire shape transcribed from `REQ-lighthouse-tenant-team-invite-
 * and-membership-endpoints`'s own answer — three deliberate deviations from
 * what was originally filed, each reasoned in that answer: `target.kind:
 * 'email'` is refused (`422`) at creation, `name` is required on ACCEPT (not
 * invite), and the roster carries no `removedAt` (a removed member is a real
 * deletion, not a soft-delete flag). Same caveat as `tenant-auth.ts`: built
 * from prose, not a readable contract file — verify against the real
 * endpoint before a live check relies on it.
 */
export const tenantInviteTargetKindSchema = z.enum(['phone', 'email'])

export const tenantInviteStatusSchema = z.enum(['pending', 'accepted', 'revoked'])

export const tenantInviteSchema = z
  .object({
    id: z.string(),
    tenantId: z.string(),
    target: z.object({ kind: tenantInviteTargetKindSchema, value: z.string() }),
    role: tenantMemberRoleSchema,
    status: tenantInviteStatusSchema,
    invitedByUserId: z.string(),
    invitedAt: z.string(),
    acceptedAt: z.string().nullable(),
    revokedAt: z.string().nullable(),
    revokedByUserId: z.string().nullable(),
  })
  .strict()
export type TenantInvite = z.infer<typeof tenantInviteSchema>

/**
 * `TST-001` finding 2. `GET /tenant-team` does not answer `{members,
 * invites}` — it answers one flat, `kind`-tagged list, live-observed:
 *
 * ```json
 * [{"kind":"member","userId":"…","inviteId":null,"name":"…","target":{...},"role":"ADMIN","status":"active"},
 *  {"kind":"invite","userId":null,"inviteId":"…","name":null,"target":{...},"role":"MANAGER","status":"pending"}]
 * ```
 *
 * Neither the original REQ nor `1C`'s answer stated a wrapper shape — this
 * is what the harness actually found, not a deviation anyone chose.
 * Confirmation asked of `1C` separately
 * (`REQ-testing-confirm-the-roster-and-accept-wire-shapes-are-the-contract`);
 * built against the observed shape now rather than leave every screen this
 * feeds broken while that's pending — same "build against what's real, flag
 * it as provisional" discipline the rest of this session has held.
 */
export const tenantTeamRowSchema = z.discriminatedUnion('kind', [
  z
    .object({
      kind: z.literal('member'),
      userId: z.string(),
      inviteId: z.null(),
      name: z.string(),
      target: z.object({ kind: tenantInviteTargetKindSchema, value: z.string() }),
      role: tenantMemberRoleSchema,
      status: z.literal('active'),
    })
    .strict(),
  z
    .object({
      kind: z.literal('invite'),
      userId: z.null(),
      inviteId: z.string(),
      name: z.string().nullable(),
      target: z.object({ kind: tenantInviteTargetKindSchema, value: z.string() }),
      role: tenantMemberRoleSchema,
      status: z.enum(['pending', 'revoked']),
    })
    .strict(),
])
export type TenantTeamRow = z.infer<typeof tenantTeamRowSchema>

export const tenantTeamListSchema = z.array(tenantTeamRowSchema)

export const createTenantInviteRequestSchema = z.object({
  target: z.object({ kind: tenantInviteTargetKindSchema, value: z.string().min(1) }),
  role: tenantMemberRoleSchema,
})
export type CreateTenantInviteRequest = z.infer<typeof createTenantInviteRequestSchema>

/** `name` is required here, not on the invite — only the invitee can state it. */
export const acceptTenantInviteRequestSchema = z.object({
  otp: z.string().min(1),
  name: z.string().min(1),
})
export type AcceptTenantInviteRequest = z.infer<typeof acceptTenantInviteRequestSchema>
